import 'project/polyfill'
import { FC } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useFlags } from 'flagsmith/react'

import LoginPage from './auth/login'
import { defaultLayout } from './_app'
import { useDevSettings } from 'common/services/useDevSettings'
import { Project } from 'common/project'
import { PlanSlug } from 'common/types/enum'
import { useUser } from 'common/services/useUser'
import PageContainer from 'components/PageContainer'
import { useHasPlan } from 'common/hooks/useHasPlan'
import StatementsCard from 'components/StatementsCard'
import { ErrorMessage } from 'components/base/Messages'
import { useUserData } from 'common/services/useUserData'
import PhoneCard from 'components/project/cards/PhoneCard'
import OffersCard from 'components/project/cards/OffersCard'
import { NextPageWithLayout } from 'types/nextPageWithLayout'
import AddressCard from 'components/project/cards/AddressCard'
import { useGetCompaniesQuery } from 'common/services/useCompany'
import MobileAppCard from 'components/project/cards/MobileAppCard'
import AccountStatusCard from 'components/project/cards/AccountStatusCard'
import AddressMigrationCard from 'components/project/cards/AddressMigrationCard'
import { useDefaultSubscription } from 'common/services/useDefaultSubscription'
import WhatsAppBusinessCard from 'components/project/cards/WhatsAppBusinessCard'

export type HomePageType = {}

type DashboardType = {}

const Dashboard: FC<DashboardType> = ({}) => {
  const router = useRouter()
  const { data: companies } = useGetCompaniesQuery({})
  const { subscriptionDetail } = useDefaultSubscription()
  const hasWhatsAppPlan = useHasPlan(PlanSlug.WHATSAPP)
  const { feature_kyc, feature_marketplace, feature_whatsapp_for_business } =
    useFlags([
      'feature_marketplace',
      'feature_kyc',
      'feature_whatsapp_for_business',
    ])
  const { userData } = useUserData()
  const { devSettings } = useDevSettings()

  if (router.query.error) {
    return (
      <>
        <Head>
          <title>Dashboard | {Project.siteName}</title>
        </Head>
        <main className='container-fluid'>
          {!!router.query.error && (
            <ErrorMessage className='text-center py-3'>
              You do not have access to the requested page, please check you are
              logged in with the correct account.
            </ErrorMessage>
          )}
        </main>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Dashboard | {Project.siteName}</title>
      </Head>
      <main className='container-fluid'>
        <h1 className='d-block mb-4'>{`${
          userData?.first_name ? `Hey ${userData.first_name}` : 'Welcome back'
        }`}</h1>

        <div className='row'>
          {feature_kyc.enabled && (
            <AccountStatusCard className='col-xl-4 pb-4 col-lg-6 col-md-12' />
          )}
          <AddressMigrationCard className='col-xl-4 pb-4 col-lg-6 col-md-12' />
          <AddressCard className='col-xl-4 pb-4 col-lg-6 col-md-12' />
          {companies?.map((v) => (
            <PhoneCard
              key={v.id}
              company_id={v.id}
              className='col-xl-4 pb-4 col-lg-6 col-md-12'
            />
          ))}
          <StatementsCard className='col-xl-4 pb-4 col-lg-6 col-md-12' />
          <MobileAppCard className='col-xl-4 pb-4 col-lg-6 col-md-12' />
          {feature_whatsapp_for_business.enabled &&
            !subscriptionDetail?.is_partner_customer &&
            !devSettings.whitelabel &&
            !hasWhatsAppPlan && (
              <WhatsAppBusinessCard
                className='col-xl-4 pb-4 col-lg-6 col-md-12'
                condensed
              />
            )}
          {feature_marketplace.enabled && <OffersCard />}
        </div>
      </main>
    </>
  )
}

const HomePage: NextPageWithLayout<HomePageType> = ({}) => {
  const { data } = useUser({})
  const router = useRouter()
  if (!data) {
    return <LoginPage />
  } else {
    if (router.query.redirect) {
      router.replace(`${router.query.redirect}`)
      return null
    }
    return <Dashboard />
  }
}

HomePage.getLayout = (page, pageProps, user) => {
  return user ? (
    defaultLayout(page, pageProps, user)
  ) : (
    <PageContainer>{page}</PageContainer>
  )
}
export default HomePage
